import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function AIDevelopmentPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI×DX システム実装・刷新",
    "description": "生成AI・LLMを用いたシステム開発・DX支援サービス。単なる開発で終わらせず、ビジネス成果を出すためのAIシステムを課題整理から設計、システム実装、マルチモーダル対応までワンチームで伴走します。",
    "provider": {
      "@type": "Organization",
      "name": "dot Blue",
      "url": siteUrl
    },
    "areaServed": "JP",
    "serviceType": "AI受託開発"
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "AIを活用したDXシステム開発・導入支援 - dot Blue",
    "description": "生成AI・LLMを用いたシステム開発・DX支援サービス。単なる開発で終わらせず、ビジネス成果を出すためのAIシステムを課題整理から設計、システム実装、マルチモーダル対応までワンチームで伴走します。",
    "url": `${siteUrl}/ai-development`,
    "publisher": {
      "@type": "Organization",
      "name": "dot Blue",
      "url": siteUrl
    }
  };

  const offerSchema = {
    "@context": "https://schema.org",
    "@type": "Offer",
    "itemOffered": {
      "@type": "Service",
      "name": "AI受託開発サービス",
      "description": "対話型AI、RAG、マルチモーダル、需要予測、データ分析など、生成AI・LLMを用いたフルスクラッチ開発とAPI連携"
    },
    "seller": {
      "@type": "Organization",
      "name": "dot Blue"
    },
    "availability": "https://schema.org/InStock",
    "priceCurrency": "JPY"
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [webPageSchema, serviceSchema, offerSchema]
  };

  return (
    <>
      <SEO
        title="AIを活用したDXシステム開発・導入支援 - dot Blue"
        description="生成AI・LLMを用いたシステム開発・DX支援サービス。単なる開発で終わらせず、ビジネス成果を出すためのAIシステムを課題整理から設計、システム実装、マルチモーダル対応までワンチームで伴走します。"
        keywords="AI受託開発, AI開発, 対話型AI, RAG, マルチモーダル, 需要予測, データ分析, 生成AI, LLM, フルスクラッチ開発, API連携, PoC, 運用改善"
        ogImage={`${siteUrl}/og-image-ai-development.jpg`}
        schema={combinedSchema}
      />
      <div className="min-h-screen bg-white">
        {/* Main Header - Global Navigation */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-between h-16 sm:h-20">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/5a08c197f8230028d9ecca8183376b65.png"
                  alt="dot Blue"
                  className="h-16 w-auto drop-shadow-md"
                />
              </Link>

              {/* Center Navigation */}
              <nav className="hidden lg:flex items-center gap-8">
                <Link
                  to="/"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33]/70 hover:text-[#0B1B33]"
                >
                  TOP
                </Link>
                <Link
                  to="/ai-insider"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33]/70 hover:text-[#0B1B33]"
                >
                  AI Insider
                </Link>
                <Link
                  to="/ai-development"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33] border-b-2 border-[#2563EB]"
                >
                  AI×DX開発
                </Link>
              </nav>

              {/* Mobile Menu */}
              <div className="lg:hidden">
                <div className="flex gap-4">
                  <Link
                    to="/"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#6B7280]"
                  >
                    TOP
                  </Link>
                  <Link
                    to="/ai-insider"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#6B7280]"
                  >
                    AI Insider
                  </Link>
                  <Link
                    to="/ai-development"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#0B1B33] border-b-2 border-[#2563EB]"
                  >
                    AI×DX開発
                  </Link>
                </div>
              </div>

              <button
                onClick={() => scrollToSection('contact')}
                className="hidden lg:inline-block px-4 sm:px-6 py-2 sm:py-2.5 bg-[#FF9900] text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-[#E68A00] transition-colors whitespace-nowrap cursor-pointer shadow-[0_6px_20px_0_rgba(0,0,0,0.08)] focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:outline-none"
              >
                無料で相談する
              </button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-32 bg-white px-4 sm:px-6">
          <div className="relative max-w-7xl mx-auto text-center">
            <h1 className="font-bold text-gray-900 mb-8 sm:mb-12 leading-tight">
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl mb-4">
                Business First,
              </span>
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
                Technology Second.
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-[#555555] mb-4 leading-relaxed max-w-4xl mx-auto">
              技術はあくまで「手段」です。
            </p>
            <p className="text-lg sm:text-xl md:text-2xl text-[#555555] leading-relaxed max-w-4xl mx-auto">
              私たちはAIのプロとして、「利益」と「課題解決」を最優先に設計します。
            </p>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-16 sm:py-24 lg:py-32 bg-[#F7F7F5] px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-8 sm:mb-12">
                Philosophy
              </h2>
            </div>
            <div className="space-y-8 text-base sm:text-lg md:text-xl text-[#555555] leading-relaxed text-center">
              <p>
                「AIを入れること」は目的ではなく手段です。<br />
                業務が楽になるか、売上が上がるか。成果が出なければ意味がないと考えています。
              </p>
              <p>
                私たちは最新技術を熟知しているからこそ、無理なAI導入は提案しません。<br />
                「今はまだAIを使うべきではない」という判断も含め、貴社にとってのベストな選択肢を正直にお伝えします。
              </p>
            </div>
          </div>
        </section>

        {/* Our Capabilities Section */}
        <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-20">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
                Our Capabilities
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12">
              {/* SENSE Block */}
              <div className="border-t-4 border-gray-900 pt-6">
                <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: 'monospace' }}>
                  SENSE
                </h3>
                <p className="text-sm text-gray-500 mb-6">見る・聞く・集める</p>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">Vision</span> ｜ 画像・動画解析
                  </li>
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">Speech</span> ｜ 音声認識
                  </li>
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">OCR / Logs</span> ｜ 書類・データ読み取り
                  </li>
                </ul>
              </div>

              {/* THINK Block */}
              <div className="border-t-4 border-gray-900 pt-6">
                <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: 'monospace' }}>
                  THINK
                </h3>
                <p className="text-sm text-gray-500 mb-6">理解・検索・推論</p>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">LLM ＋ RAG</span> ｜ ナレッジ回答
                  </li>
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">NLP</span> ｜ 要約・分類・タグ付け
                  </li>
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">Predict</span> ｜ 需要予測・不正感知
                  </li>
                </ul>
              </div>

              {/* ACT Block */}
              <div className="border-t-4 border-gray-900 pt-6">
                <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: 'monospace' }}>
                  ACT
                </h3>
                <p className="text-sm text-gray-500 mb-6">生成・最適化・自動化</p>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">Generate</span> ｜ 文章・コンテンツ作成
                  </li>
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">Optimize</span> ｜ 最適化
                  </li>
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">Agents</span> ｜ 業務プロセス自動化
                  </li>
                </ul>
              </div>

              {/* GUARD Block */}
              <div className="border-t-4 border-gray-900 pt-6">
                <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: 'monospace' }}>
                  GUARD
                </h3>
                <p className="text-sm text-gray-500 mb-6">運用・安全・拡張</p>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">LLMOps</span> ｜ 精度評価
                  </li>
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">Governance</span> ｜ リスク管理・セキュリティ
                  </li>
                  <li className="border-l-2 border-gray-300 pl-3">
                    <span className="font-semibold">Integration</span> ｜ 既存システム連携
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* WorkFlow Section */}
        <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#F7F7F5]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-20">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
                WorkFlow
              </h2>
            </div>
            <div className="flex flex-col lg:flex-row items-start justify-center gap-8 sm:gap-10 lg:gap-6 max-w-6xl mx-auto">
              {/* Step 1 */}
              <div className="flex flex-col lg:flex-row items-center w-full lg:w-auto">
                <div className="bg-white rounded-xl p-8 sm:p-10 min-w-[280px] min-h-[280px] shadow-lg w-full lg:w-auto flex flex-col">
                  <div className="text-xs font-bold text-blue-600 mb-3">STEP 1</div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">まずは聴く</h3>
                  <p className="text-sm text-[#555555] leading-relaxed flex-grow">
                    いきなり開発はしません。まずは「何に困っているか」「どうなれば理想か」をじっくりお聞きします。
                  </p>
                </div>
                <div className="hidden lg:flex items-center justify-center mx-4">
                  <i className="ri-arrow-right-line text-4xl text-blue-600 font-bold"></i>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col lg:flex-row items-center w-full lg:w-auto">
                <div className="bg-white rounded-xl p-8 sm:p-10 min-w-[280px] min-h-[280px] shadow-lg w-full lg:w-auto flex flex-col">
                  <div className="text-xs font-bold text-blue-600 mb-3">STEP 2</div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">設計図を描く</h3>
                  <p className="text-sm text-[#555555] leading-relaxed flex-grow">
                    聞いた内容をもとに、「どこをAIに任せて、どこを人がやるべきか」を考え、使いやすい仕組みを設計します。
                  </p>
                </div>
                <div className="hidden lg:flex items-center justify-center mx-4">
                  <i className="ri-arrow-right-line text-4xl text-blue-600 font-bold"></i>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col lg:flex-row items-center w-full lg:w-auto">
                <div className="bg-white rounded-xl p-8 sm:p-10 min-w-[280px] min-h-[280px] shadow-lg w-full lg:w-auto flex flex-col">
                  <div className="text-xs font-bold text-blue-600 mb-3">STEP 3</div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">作って、育てる</h3>
                  <p className="text-sm text-[#555555] leading-relaxed flex-grow">
                    最初から完璧を目指さず、拡張性を大切に作ります。実際に使いながら改善を重ね、本当に役に立つシステムに育てます。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies Section */}
        <section className="py-16 sm:py-24 bg-white px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                実装例
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="h-48 sm:h-64 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Luxury%20resort%20hotel%20with%20tropical%20beach%20setting%2C%20beautiful%20vacation%20destination%20with%20palm%20trees%20and%20ocean%20view%2C%20elegant%20hospitality%20and%20travel%20accommodation%2C%20serene%20tourism%20location%20with%20modern%20amenities%2C%20professional%20hotel%20photography%20with%20warm%20inviting%20atmosphere&width=800&height=600&seq=ota001&orientation=landscape"
                    alt="OTA旅行予約"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">自然言語検索による成約率向上</h3>
                  <p className="text-sm text-[#555555] leading-relaxed">
                    会話だけで、あなたにぴったりの宿を提案。「なぜおすすめか」という理由も語り、疑問もその場で解決。
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="h-48 sm:h-64 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Global%20e-commerce%20shopping%20concept%20with%20international%20packages%20and%20worldwide%20delivery%2C%20cross-border%20online%20retail%20business%20with%20diverse%20products%2C%20modern%20digital%20commerce%20platform%20with%20clean%20minimalist%20design%2C%20professional%20business%20photography%20showing%20global%20trade%20and%20logistics&width=800&height=600&seq=ec001&orientation=landscape"
                    alt="越境EC"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">商品説明の自動生成と多言語化</h3>
                  <p className="text-sm text-[#555555] leading-relaxed">
                    スペックだけでなく、商品の物語や魅力を多言語で語るAI。接客を通じて購入後のイメージを膨らませる。
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="h-48 sm:h-64 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Digital%20media%20publishing%20workspace%20with%20modern%20blog%20content%20creation%2C%20professional%20journalism%20and%20online%20article%20writing%20environment%2C%20contemporary%20newsroom%20with%20multiple%20screens%20showing%20web%20content%2C%20clean%20minimalist%20editorial%20office%20with%20technology%20focus&width=800&height=600&seq=media001&orientation=landscape"
                    alt="メディア・ブログ"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">記事文脈に合わせたCTA最適化</h3>
                  <p className="text-sm text-[#555555] leading-relaxed">
                    記事を学習したAIが読者の質問に回答。会話の流れから興味・関心を検知し、最適タイミングで誘導。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#F7F7F5]">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 sm:mb-8">
                Contact
              </h2>
              <div className="space-y-4 text-base sm:text-lg md:text-xl text-[#555555] leading-relaxed">
                <p>
                  「AIで何ができるかわからない」「今の業務を少しでも楽にしたい」<br />
                  そんな段階からのご相談で構いません。
                </p>
                <p>
                  AIが必要か、の判断も含めて、私たちが課題を整理し、解決策をご提案します。<br />
                  まずはお気軽に、現状の悩みをお話しください。
                </p>
              </div>
            </div>
            <form className="space-y-4 sm:space-y-6 bg-white p-6 sm:p-8 rounded-xl shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">会社名</label>
                  <input type="text" className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="株式会社〇〇" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">お名前 <span className="text-red-500">*</span></label>
                  <input type="text" required className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="山田 太郎" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">メールアドレス <span className="text-red-500">*</span></label>
                <input type="email" required className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="example@company.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">電話番号</label>
                <input type="tel" className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="03-1234-5678" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">相談内容 <span className="text-red-500">*</span></label>
                <textarea required rows={6} className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none" placeholder="AI活用についてのご相談内容をご記入ください"></textarea>
              </div>
              <button type="submit" className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-[#FF9900] text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-[#E68A00] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                無料相談・お問い合わせ
              </button>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#0B0B0C] text-white py-8 sm:py-12 lg:py-16 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-4 sm:mb-6 lg:mb-8">
              <h3 className="text-xs sm:text-sm lg:text-base font-medium text-[#E5E7EB] mb-3 sm:mb-4">リンク</h3>
              <div className="flex flex-wrap gap-3 sm:gap-4 lg:gap-6">
                <a href="/privacy-policy" className="text-xs sm:text-sm lg:text-base text-[#E5E7EB] hover:text-white transition-colors cursor-pointer hover:underline">
                  プライバシーポリシー
                </a>
                <Link to="/terms" className="text-xs sm:text-sm lg:text-base text-[#E5E7EB] hover:text-white transition-colors cursor-pointer hover:underline">
                  利用規約
                </Link>
                <Link to="/security" className="text-xs sm:text-sm lg:text-base text-[#E5E7EB] hover:text-white transition-colors cursor-pointer hover:underline">
                  セキュリティ
                </Link>
                <Link to="/company" className="text-xs sm:text-sm lg:text-base text-[#E5E7EB] hover:text-white transition-colors cursor-pointer hover:underline">
                  会社概要
                </Link>
              </div>
            </div>
            
            <div className="pt-4 sm:pt-6 lg:pt-8 border-t border-[#374151] flex flex-col lg:flex-row justify-between items-center gap-2 sm:gap-3 lg:gap-4">
              <p className="text-xs sm:text-sm lg:text-base text-[#E5E7EB]">Copyright © dot Blue Inc. All Rights Reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
